import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";
import { useEffect, useState } from "react";
import { clientService } from "@/services";

interface ContractsSectionProps {
  serviceOrder: ServiceOrderDto;
}

function ContractViewer({
  contractType,
  serviceOrderId,
}: {
  contractType: "msa" | "sow";
  serviceOrderId: string;
}) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchContractUrl() {
      try {
        setLoading(true);
        setError(null);
        const url = await clientService.getContractUrl(
          serviceOrderId,
          contractType
        );
        setPdfUrl(url);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load contract"
        );
      } finally {
        setLoading(false);
      }
    }

    fetchContractUrl();
  }, [contractType, serviceOrderId]);

  if (loading) {
    return (
      <div className="aspect-[16/10] bg-muted/20 flex items-center justify-center">
        <p className="text-muted-foreground">Loading contract...</p>
      </div>
    );
  }

  if (error || !pdfUrl) {
    return (
      <div className="aspect-[16/10] bg-muted/20 flex items-center justify-center">
        <p className="text-destructive">{error || "Contract not available"}</p>
      </div>
    );
  }

  return (
    <div className="aspect-[16/10] bg-muted/20">
      <iframe
        src={pdfUrl}
        className="w-full h-full border-0"
        title={`${contractType.toUpperCase()} Contract`}
      />
    </div>
  );
}

export function ContractsSection({ serviceOrder }: ContractsSectionProps) {
  return (
    <div className="space-y-6">
      <Tabs defaultValue="msa" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="msa">Master Service Agreement</TabsTrigger>
          <TabsTrigger value="sow">Statement of Work</TabsTrigger>
        </TabsList>

        <TabsContent value="msa">
          <Card className="glass-card border-gradient overflow-hidden">
            <ContractViewer
              contractType="msa"
              serviceOrderId={serviceOrder.id}
            />
          </Card>
        </TabsContent>

        <TabsContent value="sow">
          <Card className="glass-card border-gradient overflow-hidden">
            <ContractViewer
              contractType="sow"
              serviceOrderId={serviceOrder.id}
            />
          </Card>
        </TabsContent>
      </Tabs>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Project Start</p>
            <p className="font-semibold">
              {new Date(
                serviceOrder.project.timeline.startDate
              ).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Project End</p>
            <p className="font-semibold">
              {new Date(
                serviceOrder.project.timeline.endDate
              ).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
